/**
 * MCP MetaInfo Declare Approval Dialog
 *
 * Shown when an AI agent proposes ONE MetaInfo declaration and
 * `metaInfoWritePermission` is `'confirm'`.
 *
 * The whole job of this dialog is to say, in plain words, what the agent wants
 * to save and what it will do — a user who cannot tell `loose_fk` from
 * `reference_table` still has to be able to answer. So each kind gets one
 * sentence naming the real tables and columns, and every dialog carries the two
 * facts that decide whether approving is safe: nothing in the database changes,
 * and the entry will be badged as AI-written until the user edits it.
 */

import { Bot, Clock, Database, Tag } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import type { McpMetaInfoDeclareApprovalRequest } from '../../../types/mcpUiControl';
import {
  DECLARE_KIND_LABELS,
  describeConsequence,
  describeDeclaration,
} from './metaInfoDeclareCopy';

interface McpMetaInfoDeclareApprovalDialogProps {
  request: McpMetaInfoDeclareApprovalRequest | null;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
}

export function McpMetaInfoDeclareApprovalDialog({
  request,
  onApprove,
  onDeny,
}: McpMetaInfoDeclareApprovalDialogProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Read through a ref so the countdown effect depends on the request alone —
  // a new `onDeny` identity must not restart the clock.
  const onDenyRef = useRef(onDeny);
  onDenyRef.current = onDeny;

  useEffect(() => {
    if (!request) {
      setTimeRemaining(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const endTime = Date.now() + request.timeoutMs;
    setTimeRemaining(Math.ceil((endTime - Date.now()) / 1000));

    timerRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setTimeRemaining(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        // Main has already resolved this request as a timeout and forgotten
        // it, so the dialog is now a prompt that decides nothing. Close it:
        // the answer it would send is ignored either way, and leaving it up
        // both misleads the user and blocks the next proposal, which is
        // refused while a request is pending. (`denyRequest` clears the slot;
        // its response lands on a requestId main no longer knows.)
        onDenyRef.current(request.requestId);
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [request]);

  if (!request) {
    return null;
  }

  const handleApprove = () => onApprove(request.requestId);
  const handleDeny = () => onDeny(request.requestId);

  return (
    <AlertDialog open={!!request} onOpenChange={(open) => !open && handleDeny()}>
      <AlertDialogContent className="max-w-lg" data-testid="metainfo-declare-approval-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <Bot className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg">
                AI agent wants to save database metadata
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                Review what it proposes before approving
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="secondary">{DECLARE_KIND_LABELS[request.kind]}</Badge>
            <span className="text-xs text-muted-foreground">
              {request.connectionAlias} · {request.database}
            </span>
          </div>

          <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Tag className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
              <div className="space-y-2 text-sm">
                <p data-testid="metainfo-declare-approval-summary">
                  {describeDeclaration(request)}
                </p>
                {request.description && (
                  <p className="text-xs text-muted-foreground italic">
                    Agent's note: {request.description}
                  </p>
                )}
                {request.isUpdate && (
                  <p className="text-xs text-warning">
                    This replaces the entry already saved for {request.target}.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Database className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span data-testid="metainfo-declare-approval-provenance">
              {describeConsequence(request)}
            </span>
          </div>

          {timeRemaining !== null && timeRemaining <= 30 && (
            <div className="flex items-center gap-2 text-xs text-warning">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {timeRemaining > 0
                  ? `Request expires in ${timeRemaining}s (will be denied)`
                  : 'Request expired'}
              </span>
            </div>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDeny}
            data-testid="metainfo-declare-approval-deny"
          >
            Deny
          </Button>
          <Button
            variant="default"
            onClick={handleApprove}
            data-testid="metainfo-declare-approval-approve"
            disabled={timeRemaining === 0}
          >
            Approve
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
